import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  GraduationCap, 
  User, 
  History, 
  BookmarkCheck, 
  TrendingUp, 
  LogOut,
  FileText,
  Calendar,
  MapPin,
  Award,
  ChevronRight,
  RefreshCw,
  Building,
  IndianRupee,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  BookOpen,
  ClipboardList,
  Bell,
  Save,
  AlertCircle,
  Info,
  Trash2,
  Sparkles,
  Phone,
  Video,
  Star,
  Zap,
  Crown,
  ArrowRight,
  Gift,
  TrendingDown,
  Users
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState([]);
  const [savedColleges, setSavedColleges] = useState([]);
  const [testAttempts, setTestAttempts] = useState([]);
  const [essentialInfo, setEssentialInfo] = useState(null);
  const [assignedPages, setAssignedPages] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [savingInfo, setSavingInfo] = useState(false);

  useEffect(() => {
    // Check if user is logged in - support both 'user' and 'userToken' localStorage keys
    const userData = localStorage.getItem('user');
    const userToken = localStorage.getItem('userToken');
    
    if (!userData && !userToken) {
      toast({
        title: "Welcome to your Dashboard!",
        description: "Please sign in to view your predictions, saved colleges, and test progress",
        variant: "default"
      });
      navigate('/login');
      return;
    }
    
    // Try to get user from 'user' key first, fallback to constructing from individual keys
    let parsedUser;
    if (userData) {
      try {
        parsedUser = JSON.parse(userData);
      } catch (e) {
        console.error('Error parsing user data:', e);
        // Fallback to individual keys
        parsedUser = null;
      }
    }
    
    if (!parsedUser) {
      // Construct user object from individual localStorage keys
      parsedUser = {
        id: localStorage.getItem('userId') || 'guest-' + Date.now(),
        name: localStorage.getItem('userName') || 'Student',
        email: localStorage.getItem('userEmail') || '',
        mobile: localStorage.getItem('userMobile') || '',
        referral_code: localStorage.getItem('userReferralCode') || '',
        created_at: null
      };
    }
    
    // Ensure user has an id
    if (!parsedUser.id) {
      parsedUser.id = 'guest-' + Date.now();
    }
    
    setUser(parsedUser);
    fetchDashboardData(parsedUser.id);
  }, [navigate]);

  const fetchDashboardData = async (userId) => {
    setLoading(true);
    
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setLoading(false);
      console.warn('Dashboard data fetch timed out');
    }, 10000); // 10 second timeout
    
    try {
      // Fetch all data in parallel with individual error handling
      const [predictionsRes, savedRes, testsRes, infoRes, pagesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/user/predictions?user_id=${userId}`, { timeout: 8000 }).catch((err) => {
          console.error('Error fetching predictions:', err);
          return { data: { predictions: [] } };
        }),
        axios.get(`${BACKEND_URL}/api/user/saved-colleges?user_id=${userId}`, { timeout: 8000 }).catch((err) => {
          console.error('Error fetching saved colleges:', err);
          return { data: { colleges: [] } };
        }),
        axios.get(`${BACKEND_URL}/api/user/test-attempts?user_id=${userId}`, { timeout: 8000 }).catch((err) => {
          console.error('Error fetching test attempts:', err);
          return { data: { attempts: [] } };
        }),
        axios.get(`${BACKEND_URL}/api/user/essential-info?user_id=${userId}`, { timeout: 8000 }).catch((err) => {
          console.error('Error fetching essential info:', err);
          return { data: { info: null } };
        }),
        axios.get(`${BACKEND_URL}/api/user/assigned-pages?user_id=${userId}`, { timeout: 8000 }).catch((err) => {
          console.error('Error fetching assigned pages:', err);
          return { data: { pages: [] } };
        })
      ]);

      if (predictionsRes.data.success !== false) {
        setPredictions(predictionsRes.data.predictions || []);
      }
      if (savedRes.data.success !== false) {
        setSavedColleges(savedRes.data.colleges || []);
      }
      if (testsRes.data.success !== false) {
        setTestAttempts(testsRes.data.attempts || []);
      }
      if (infoRes.data.info) {
        setEssentialInfo(infoRes.data.info);
      } else {
        // Initialize with empty info
        setEssentialInfo({
          full_name: user?.name || '',
          email: user?.email || '',
          mobile: user?.mobile || ''
        });
      }
      if (pagesRes.data.success !== false) {
        setAssignedPages(pagesRes.data.pages || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear all user-related localStorage keys
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userId');
    localStorage.removeItem('userMobile');
    localStorage.removeItem('userReferralCode');
    localStorage.removeItem('userEarnings');
    toast({ title: "Logged Out", description: "You have been logged out successfully" });
    navigate('/');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getChanceColor = (chance) => {
    switch (chance) {
      case 'High': return 'bg-green-100 text-green-700';
      case 'Moderate': return 'bg-yellow-100 text-yellow-700';
      case 'Low': return 'bg-orange-100 text-orange-700';
      default: return 'bg-red-100 text-red-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'normal': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const handleSaveEssentialInfo = async () => {
    if (!user?.id) return;
    
    setSavingInfo(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/user/essential-info?user_id=${user.id}`,
        essentialInfo
      );
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Your essential information has been saved"
        });
      }
    } catch (error) {
      console.error('Error saving essential info:', error);
      toast({
        title: "Error",
        description: "Failed to save information. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSavingInfo(false);
    }
  };

  const handleRemoveSavedCollege = async (collegeId) => {
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/user/saved-colleges/${collegeId}?user_id=${user.id}`
      );
      
      if (response.data.success) {
        setSavedColleges(prev => prev.filter(c => c.id !== collegeId && c.college_id !== collegeId));
        toast({
          title: "Removed",
          description: "College removed from saved list"
        });
      }
    } catch (error) {
      console.error('Error removing college:', error);
      toast({
        title: "Error",
        description: "Failed to remove college",
        variant: "destructive"
      });
    }
  };

  const handleMarkPageAsRead = async (pageId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/user/assigned-pages/${pageId}/read?user_id=${user.id}`
      );
      
      setAssignedPages(prev => prev.map(p => 
        p.id === pageId ? { ...p, is_read: true } : p
      ));
    } catch (error) {
      console.error('Error marking page as read:', error);
    }
  };

  const updateEssentialInfo = (field, value) => {
    setEssentialInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const unreadPages = assignedPages.filter(p => !p.is_read);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-slate-900">Student Dashboard</h1>
                <p className="text-xs text-slate-600">Welcome, {user?.name || 'Student'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {unreadPages.length > 0 && (
                <Badge variant="destructive" className="animate-pulse">
                  {unreadPages.length} New
                </Badge>
              )}
              <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                Home
              </Button>
              <Button variant="destructive" size="sm" onClick={handleLogout} className="gap-1">
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs sm:text-sm">Predictions</p>
                  <p className="text-2xl font-bold">{predictions.length}</p>
                </div>
                <History className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs sm:text-sm">Saved</p>
                  <p className="text-2xl font-bold">{savedColleges.length}</p>
                </div>
                <BookmarkCheck className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs sm:text-sm">Tests</p>
                  <p className="text-2xl font-bold">{testAttempts.length}</p>
                </div>
                <FileText className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-xs sm:text-sm">Avg Score</p>
                  <p className="text-2xl font-bold">
                    {testAttempts.length > 0 
                      ? Math.round(testAttempts.reduce((sum, t) => sum + (t.percentage || 0), 0) / testAttempts.length)
                      : 0}%
                  </p>
                </div>
                <Award className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rose-100 text-xs sm:text-sm">Notices</p>
                  <p className="text-2xl font-bold">{unreadPages.length}</p>
                </div>
                <Bell className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4">
            <TabsList className="inline-flex min-w-max">
              <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger value="predictions" className="gap-2" data-testid="tab-predictions">
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Predictions</span>
              </TabsTrigger>
              <TabsTrigger value="saved" className="gap-2" data-testid="tab-saved">
                <BookmarkCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Saved</span>
              </TabsTrigger>
              <TabsTrigger value="tests" className="gap-2" data-testid="tab-tests">
                <BookOpen className="w-4 h-4" />
                <span className="hidden sm:inline">Tests</span>
              </TabsTrigger>
              <TabsTrigger value="essential" className="gap-2" data-testid="tab-essential">
                <ClipboardList className="w-4 h-4" />
                <span className="hidden sm:inline">Essential Info</span>
              </TabsTrigger>
              <TabsTrigger value="notices" className="gap-2 relative" data-testid="tab-notices">
                <Bell className="w-4 h-4" />
                <span className="hidden sm:inline">Notices</span>
                {unreadPages.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                    {unreadPages.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Profile Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Profile Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {user?.name?.charAt(0).toUpperCase() || 'S'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{user?.name || 'Student'}</h3>
                      <p className="text-slate-600 text-sm">{user?.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <p className="text-xs text-slate-500">Mobile</p>
                      <p className="font-medium">{user?.mobile || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Member Since</p>
                      <p className="font-medium">{formatDate(user?.created_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-600" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {predictions.length > 0 ? (
                    <div className="space-y-3">
                      {predictions.slice(0, 3).map((pred, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Target className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">Prediction for Rank {pred.rank}</p>
                            <p className="text-xs text-slate-500">{formatDate(pred.created_at)}</p>
                          </div>
                          <Badge variant="outline">{pred.category}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No recent activity</p>
                      <Button className="mt-4" onClick={() => navigate('/')} data-testid="get-first-prediction-btn">
                        Get Your First Prediction
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/')} data-testid="quick-action-prediction">
                    <Target className="w-6 h-6 text-blue-600" />
                    <span className="text-xs">New Prediction</span>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/test-series')} data-testid="quick-action-tests">
                    <BookOpen className="w-6 h-6 text-purple-600" />
                    <span className="text-xs">Practice Tests</span>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setActiveTab('saved')} data-testid="quick-action-saved">
                    <BookmarkCheck className="w-6 h-6 text-green-600" />
                    <span className="text-xs">Saved Colleges</span>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/guide')} data-testid="quick-action-guide">
                    <FileText className="w-6 h-6 text-orange-600" />
                    <span className="text-xs">Form Guide</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Premium Services Promotion */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Expert Counseling CTA */}
              <Card className="relative overflow-hidden border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-yellow-400/20 to-amber-400/20 rounded-full" />
                <CardHeader className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                      <Crown className="w-3 h-3 mr-1" />
                      Premium
                    </Badge>
                    <Badge variant="outline" className="bg-white/60 text-amber-700 border-amber-300">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Limited Slots
                    </Badge>
                  </div>
                  <CardTitle className="text-xl text-amber-900 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-amber-600" />
                    1-on-1 Expert Counseling
                  </CardTitle>
                  <CardDescription className="text-amber-700">
                    Get personalized guidance from IIT/NIT alumni experts
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative">
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-sm text-amber-800">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Personalized college selection strategy
                    </li>
                    <li className="flex items-center gap-2 text-sm text-amber-800">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Choice filling guidance (JoSAA/CSAB)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-amber-800">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Doubt resolution via call/chat
                    </li>
                    <li className="flex items-center gap-2 text-sm text-amber-800">
                      <Video className="w-4 h-4 text-blue-600" />
                      Video call sessions available
                    </li>
                  </ul>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-amber-600 line-through">Starting ₹2,999</p>
                      <p className="text-2xl font-bold text-amber-900">₹999 <span className="text-sm font-normal">onwards</span></p>
                    </div>
                    <Button 
                      onClick={() => navigate('/expert-counseling')} 
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-2"
                      data-testid="book-counseling-btn"
                    >
                      Book Now
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Test Series Premium CTA */}
              <Card className="relative overflow-hidden border-2 border-purple-200 bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-400/20 to-indigo-400/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full" />
                <CardHeader className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-0">
                      <Zap className="w-3 h-3 mr-1" />
                      JEE Ranker
                    </Badge>
                    <Badge variant="outline" className="bg-white/60 text-purple-700 border-purple-300">
                      <Star className="w-3 h-3 mr-1" />
                      Top Rated
                    </Badge>
                  </div>
                  <CardTitle className="text-xl text-purple-900 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    JEE Ranker Booster
                  </CardTitle>
                  <CardDescription className="text-purple-700">
                    Crack JEE with our expertly crafted test series
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative">
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-sm text-purple-800">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      500+ practice questions (Physics, Chemistry, Maths)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-purple-800">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      JEE Main & Advanced pattern tests
                    </li>
                    <li className="flex items-center gap-2 text-sm text-purple-800">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Detailed solutions & explanations
                    </li>
                    <li className="flex items-center gap-2 text-sm text-purple-800">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      Performance analytics & rank prediction
                    </li>
                  </ul>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-purple-600 line-through">₹1,499/year</p>
                      <p className="text-2xl font-bold text-purple-900">₹499 <span className="text-sm font-normal">/year</span></p>
                    </div>
                    <Button 
                      onClick={() => navigate('/test-series')} 
                      className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white gap-2"
                      data-testid="start-tests-btn"
                    >
                      Start Tests
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Special Offer Banner */}
            <Card className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                      <Gift className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold flex items-center gap-2">
                        Bundle & Save 40%!
                        <Badge className="bg-yellow-400 text-yellow-900 border-0 animate-pulse">
                          Limited Time
                        </Badge>
                      </h3>
                      <p className="text-emerald-100 text-sm">
                        Get Expert Counseling + Premium Test Series at a special bundled price
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-emerald-200 line-through text-sm">₹2,498</p>
                      <p className="text-3xl font-bold">₹1,499</p>
                    </div>
                    <Button 
                      variant="secondary" 
                      className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold gap-2"
                      onClick={() => navigate('/expert-counseling')}
                      data-testid="bundle-offer-btn"
                    >
                      Grab Bundle
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Social Proof */}
            <Card className="bg-slate-900 text-white border-0">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div>
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Users className="w-5 h-5 text-blue-400" />
                      <p className="text-3xl font-bold text-blue-400">10,000+</p>
                    </div>
                    <p className="text-slate-400 text-sm">Students Guided</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Star className="w-5 h-5 text-yellow-400" />
                      <p className="text-3xl font-bold text-yellow-400">4.8</p>
                    </div>
                    <p className="text-slate-400 text-sm">Average Rating</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      <p className="text-3xl font-bold text-green-400">95%</p>
                    </div>
                    <p className="text-slate-400 text-sm">Success Rate</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Award className="w-5 h-5 text-purple-400" />
                      <p className="text-3xl font-bold text-purple-400">500+</p>
                    </div>
                    <p className="text-slate-400 text-sm">IIT Selections</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notices Preview */}
            {unreadPages.length > 0 && (
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <AlertCircle className="w-5 h-5" />
                    New Notices ({unreadPages.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {unreadPages.slice(0, 2).map((page) => (
                      <div key={page.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-2">
                          <Badge className={getPriorityColor(page.priority)} variant="secondary">
                            {page.priority}
                          </Badge>
                          <span className="font-medium text-sm">{page.title}</span>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setActiveTab('notices')}>
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Predictions Tab */}
          <TabsContent value="predictions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Your Prediction History</CardTitle>
                <CardDescription>All your college predictions</CardDescription>
              </CardHeader>
              <CardContent>
                {predictions.length > 0 ? (
                  <div className="space-y-4">
                    {predictions.map((pred, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`prediction-item-${idx}`}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-blue-100 text-blue-700">Rank: {pred.rank}</Badge>
                              <Badge variant="outline">{pred.category}</Badge>
                              <Badge variant="outline">{pred.exam_type || 'JEE Main'}</Badge>
                            </div>
                            <p className="text-sm text-slate-600">
                              {pred.counselling} • {pred.home_state || 'All India'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">{formatDate(pred.created_at)}</p>
                            <p className="font-medium text-green-600">{pred.matched_colleges || 0} matches</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No predictions yet</p>
                    <p className="text-sm mb-4">Start by getting your first college prediction</p>
                    <Button onClick={() => navigate('/')} data-testid="get-prediction-btn">Get Prediction Now</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Saved Colleges Tab */}
          <TabsContent value="saved" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Saved Colleges</CardTitle>
                <CardDescription>Colleges you've bookmarked for later</CardDescription>
              </CardHeader>
              <CardContent>
                {savedColleges.length > 0 ? (
                  <div className="space-y-4">
                    {savedColleges.map((college, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`saved-college-${idx}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-semibold">{college.name}</h4>
                            <p className="text-sm text-slate-600 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {college.city}, {college.state}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline">{college.type}</Badge>
                              {college.branch && <Badge variant="secondary">{college.branch}</Badge>}
                            </div>
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            {college.fees && (
                              <p className="text-sm font-medium flex items-center gap-1 text-green-600">
                                <IndianRupee className="w-3 h-3" />
                                {college.fees.min ? `${(college.fees.min / 100000).toFixed(1)}L - ${(college.fees.max / 100000).toFixed(1)}L` : 'N/A'}
                              </p>
                            )}
                            {college.chance && (
                              <Badge className={getChanceColor(college.chance)}>
                                {college.chance} Chance
                              </Badge>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveSavedCollege(college.id || college.college_id)}
                              data-testid={`remove-saved-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <BookmarkCheck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No saved colleges</p>
                    <p className="text-sm mb-4">Save colleges from your prediction results</p>
                    <Button onClick={() => navigate('/')} data-testid="get-predictions-btn">Get Predictions</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Series Tab */}
          <TabsContent value="tests" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Test Series Progress</CardTitle>
                <CardDescription>Your JEE Ranker Booster test history</CardDescription>
              </CardHeader>
              <CardContent>
                {testAttempts.length > 0 ? (
                  <div className="space-y-4">
                    {testAttempts.map((attempt, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`test-attempt-${idx}`}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              attempt.percentage >= 70 ? 'bg-green-100' : 
                              attempt.percentage >= 40 ? 'bg-yellow-100' : 'bg-red-100'
                            }`}>
                              {attempt.percentage >= 70 ? (
                                <CheckCircle className="w-6 h-6 text-green-600" />
                              ) : attempt.percentage >= 40 ? (
                                <Clock className="w-6 h-6 text-yellow-600" />
                              ) : (
                                <XCircle className="w-6 h-6 text-red-600" />
                              )}
                            </div>
                            <div>
                              <h4 className="font-semibold">{attempt.test_name || 'Practice Test'}</h4>
                              <p className="text-sm text-slate-600">{formatDate(attempt.completed_at)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-blue-600">{attempt.percentage || 0}%</p>
                            <p className="text-xs text-slate-500">
                              {attempt.correct || 0}/{attempt.total || 0} correct
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No tests taken yet</p>
                    <p className="text-sm mb-4">Start practicing with our JEE test series</p>
                    <Button onClick={() => navigate('/test-series')} data-testid="start-practicing-btn">Start Practicing</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Essential Info Tab */}
          <TabsContent value="essential" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  Essential Information for Form Filling
                </CardTitle>
                <CardDescription>
                  Save your details here to quickly fill counselling forms. This data is stored securely and can be copied when needed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Personal Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <User className="w-4 h-4" /> Personal Details
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="full_name">Full Name (as per documents)</Label>
                        <Input
                          id="full_name"
                          value={essentialInfo?.full_name || ''}
                          onChange={(e) => updateEssentialInfo('full_name', e.target.value)}
                          placeholder="Enter full name"
                          data-testid="input-full-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="father_name">Father's Name</Label>
                        <Input
                          id="father_name"
                          value={essentialInfo?.father_name || ''}
                          onChange={(e) => updateEssentialInfo('father_name', e.target.value)}
                          placeholder="Enter father's name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mother_name">Mother's Name</Label>
                        <Input
                          id="mother_name"
                          value={essentialInfo?.mother_name || ''}
                          onChange={(e) => updateEssentialInfo('mother_name', e.target.value)}
                          placeholder="Enter mother's name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="dob">Date of Birth</Label>
                        <Input
                          id="dob"
                          type="date"
                          value={essentialInfo?.date_of_birth || ''}
                          onChange={(e) => updateEssentialInfo('date_of_birth', e.target.value)}
                          data-testid="input-dob"
                        />
                      </div>
                      <div>
                        <Label htmlFor="gender">Gender</Label>
                        <Select value={essentialInfo?.gender || ''} onValueChange={(v) => updateEssentialInfo('gender', v)}>
                          <SelectTrigger data-testid="select-gender">
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="category">Category</Label>
                        <Select value={essentialInfo?.category || ''} onValueChange={(v) => updateEssentialInfo('category', v)}>
                          <SelectTrigger data-testid="select-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="General">General</SelectItem>
                            <SelectItem value="OBC-NCL">OBC-NCL</SelectItem>
                            <SelectItem value="SC">SC</SelectItem>
                            <SelectItem value="ST">ST</SelectItem>
                            <SelectItem value="EWS">EWS</SelectItem>
                            <SelectItem value="PwD">PwD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="home_state">Home State</Label>
                        <Input
                          id="home_state"
                          value={essentialInfo?.home_state || ''}
                          onChange={(e) => updateEssentialInfo('home_state', e.target.value)}
                          placeholder="State as per documents"
                        />
                      </div>
                      <div>
                        <Label htmlFor="aadhar">Aadhar Number</Label>
                        <Input
                          id="aadhar"
                          value={essentialInfo?.aadhar_number || ''}
                          onChange={(e) => updateEssentialInfo('aadhar_number', e.target.value)}
                          placeholder="12-digit Aadhar number"
                          maxLength={12}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Academic Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4" /> Academic Details
                    </h3>
                    <div className="grid md:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="tenth_board">10th Board</Label>
                        <Input
                          id="tenth_board"
                          value={essentialInfo?.tenth_board || ''}
                          onChange={(e) => updateEssentialInfo('tenth_board', e.target.value)}
                          placeholder="e.g., CBSE, ICSE"
                        />
                      </div>
                      <div>
                        <Label htmlFor="tenth_year">10th Passing Year</Label>
                        <Input
                          id="tenth_year"
                          value={essentialInfo?.tenth_year || ''}
                          onChange={(e) => updateEssentialInfo('tenth_year', e.target.value)}
                          placeholder="e.g., 2022"
                        />
                      </div>
                      <div>
                        <Label htmlFor="tenth_percentage">10th Percentage/CGPA</Label>
                        <Input
                          id="tenth_percentage"
                          value={essentialInfo?.tenth_percentage || ''}
                          onChange={(e) => updateEssentialInfo('tenth_percentage', e.target.value)}
                          placeholder="e.g., 95% or 9.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="tenth_school">10th School Name</Label>
                        <Input
                          id="tenth_school"
                          value={essentialInfo?.tenth_school || ''}
                          onChange={(e) => updateEssentialInfo('tenth_school', e.target.value)}
                          placeholder="School name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="twelfth_board">12th Board</Label>
                        <Input
                          id="twelfth_board"
                          value={essentialInfo?.twelfth_board || ''}
                          onChange={(e) => updateEssentialInfo('twelfth_board', e.target.value)}
                          placeholder="e.g., CBSE, State Board"
                        />
                      </div>
                      <div>
                        <Label htmlFor="twelfth_year">12th Passing Year</Label>
                        <Input
                          id="twelfth_year"
                          value={essentialInfo?.twelfth_year || ''}
                          onChange={(e) => updateEssentialInfo('twelfth_year', e.target.value)}
                          placeholder="e.g., 2024"
                        />
                      </div>
                      <div>
                        <Label htmlFor="twelfth_percentage">12th Percentage</Label>
                        <Input
                          id="twelfth_percentage"
                          value={essentialInfo?.twelfth_percentage || ''}
                          onChange={(e) => updateEssentialInfo('twelfth_percentage', e.target.value)}
                          placeholder="e.g., 85%"
                        />
                      </div>
                      <div>
                        <Label htmlFor="twelfth_school">12th School Name</Label>
                        <Input
                          id="twelfth_school"
                          value={essentialInfo?.twelfth_school || ''}
                          onChange={(e) => updateEssentialInfo('twelfth_school', e.target.value)}
                          placeholder="School name"
                        />
                      </div>
                    </div>
                  </div>

                  {/* JEE Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Target className="w-4 h-4" /> JEE Details
                    </h3>
                    <div className="grid md:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="jee_main_roll">JEE Main Roll Number</Label>
                        <Input
                          id="jee_main_roll"
                          value={essentialInfo?.jee_main_roll || ''}
                          onChange={(e) => updateEssentialInfo('jee_main_roll', e.target.value)}
                          placeholder="JEE Main roll number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="jee_main_rank">JEE Main AIR</Label>
                        <Input
                          id="jee_main_rank"
                          value={essentialInfo?.jee_main_rank || ''}
                          onChange={(e) => updateEssentialInfo('jee_main_rank', e.target.value)}
                          placeholder="All India Rank"
                        />
                      </div>
                      <div>
                        <Label htmlFor="jee_adv_roll">JEE Advanced Roll Number</Label>
                        <Input
                          id="jee_adv_roll"
                          value={essentialInfo?.jee_advanced_roll || ''}
                          onChange={(e) => updateEssentialInfo('jee_advanced_roll', e.target.value)}
                          placeholder="JEE Advanced roll number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="jee_adv_rank">JEE Advanced AIR</Label>
                        <Input
                          id="jee_adv_rank"
                          value={essentialInfo?.jee_advanced_rank || ''}
                          onChange={(e) => updateEssentialInfo('jee_advanced_rank', e.target.value)}
                          placeholder="All India Rank"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Contact Details
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="contact_mobile">Mobile Number</Label>
                        <Input
                          id="contact_mobile"
                          value={essentialInfo?.mobile || ''}
                          onChange={(e) => updateEssentialInfo('mobile', e.target.value)}
                          placeholder="10-digit mobile number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_email">Email Address</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={essentialInfo?.email || ''}
                          onChange={(e) => updateEssentialInfo('email', e.target.value)}
                          placeholder="Email address"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pincode">Pincode</Label>
                        <Input
                          id="pincode"
                          value={essentialInfo?.pincode || ''}
                          onChange={(e) => updateEssentialInfo('pincode', e.target.value)}
                          placeholder="6-digit pincode"
                          maxLength={6}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Label htmlFor="address">Full Address</Label>
                        <Textarea
                          id="address"
                          value={essentialInfo?.address || ''}
                          onChange={(e) => updateEssentialInfo('address', e.target.value)}
                          placeholder="Complete address with city and state"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bank Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <IndianRupee className="w-4 h-4" /> Bank Details (for fee payment)
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="bank_name">Bank Name</Label>
                        <Input
                          id="bank_name"
                          value={essentialInfo?.bank_name || ''}
                          onChange={(e) => updateEssentialInfo('bank_name', e.target.value)}
                          placeholder="Bank name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="account_number">Account Number</Label>
                        <Input
                          id="account_number"
                          value={essentialInfo?.account_number || ''}
                          onChange={(e) => updateEssentialInfo('account_number', e.target.value)}
                          placeholder="Bank account number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ifsc">IFSC Code</Label>
                        <Input
                          id="ifsc"
                          value={essentialInfo?.ifsc_code || ''}
                          onChange={(e) => updateEssentialInfo('ifsc_code', e.target.value)}
                          placeholder="e.g., SBIN0001234"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t">
                    <Button 
                      onClick={handleSaveEssentialInfo} 
                      disabled={savingInfo}
                      className="gap-2"
                      data-testid="save-essential-info-btn"
                    >
                      {savingInfo ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {savingInfo ? 'Saving...' : 'Save Information'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notices Tab */}
          <TabsContent value="notices" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-orange-600" />
                  Notices & Updates
                </CardTitle>
                <CardDescription>Important notices and updates from admin</CardDescription>
              </CardHeader>
              <CardContent>
                {assignedPages.length > 0 ? (
                  <div className="space-y-4">
                    {assignedPages.map((page, idx) => (
                      <div 
                        key={page.id} 
                        className={`p-4 border rounded-lg transition-colors ${
                          page.is_read ? 'bg-slate-50' : 'bg-yellow-50 border-yellow-200'
                        }`}
                        data-testid={`notice-item-${idx}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={getPriorityColor(page.priority)}>
                                {page.priority}
                              </Badge>
                              <Badge variant="outline">{page.page_type}</Badge>
                              {!page.is_read && (
                                <Badge variant="destructive" className="animate-pulse">NEW</Badge>
                              )}
                            </div>
                            <h4 className="font-semibold text-lg">{page.title}</h4>
                            <p className="text-slate-600 mt-2 whitespace-pre-wrap">{page.content}</p>
                            <p className="text-xs text-slate-400 mt-2">{formatDate(page.created_at)}</p>
                          </div>
                          {!page.is_read && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleMarkPageAsRead(page.id)}
                              data-testid={`mark-read-${idx}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Mark Read
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Bell className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No notices yet</p>
                    <p className="text-sm">You'll see important updates here when available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StudentDashboard;
